import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../components/Navbar";
import Calendar from "../components/Calendar";
import { api } from "../api";
import { Target, MapPin, CalendarDays, Clock, Timer, CalendarCheck2 } from "lucide-react";
import "./PlanDetailPage.css";

function skipKeyFor(user) {
  const id = user?.id || user?.email || "anon";
  return `plan_onboarding_skipped_v1:${id}`;
}

export default function PlanDetailPage({ user, onLogout }) {
  const nav = useNavigate();
  const { planId } = useParams();
  const { t } = useTranslation();

  const [plan, setPlan] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  async function loadPlan() {
    try {
      setLoading(true);

      const res = await api("/me/plans", { cacheTtlMs: 60_000 });
      const all = Array.isArray(res?.plans) ? res.plans : [];
      const found = all.find((p) => String(p.id) === String(planId));

      if (!found) {
        setPlan(null);
        setPreview(null);
        return;
      }

      setPlan(found);

      const sch = await api(`/me/plans/${encodeURIComponent(planId)}/schedule`, {
        cacheTtlMs: 60_000,
      });
      setPreview(sch?.schedule ?? null);
    } catch (err) {
      console.error("Failed to load plan detail", err);
      setPlan(null);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  const marked = useMemo(() => {
    const map = new Map();
    if (!plan?.planStart || !plan?.planDuration) return map;

    const start = new Date(`${plan.planStart}T12:00:00`);
    const end = new Date(start);
    end.setMonth(end.getMonth() + Number(plan.planDuration || 0));

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const ymd = d.toISOString().slice(0, 10);
      const entry = preview?.[ymd];
      map.set(ymd, {
        count: entry?.exercises?.length || 0,
        type: entry?.dayType || "rest",
      });
    }
    return map;
  }, [plan, preview]);

  const endDate = useMemo(() => {
    if (!plan?.planStart || !plan?.planDuration) return null;
    const d = new Date(`${plan.planStart}T12:00:00`);
    d.setMonth(d.getMonth() + Number(plan.planDuration || 0));
    return d.toISOString().slice(0, 10);
  }, [plan]);

  const monthsLabel = useMemo(() => {
    const n = Number(plan?.planDuration) || 0;
    return t("plan.months", { count: n });
  }, [plan?.planDuration, t]);

  function goCreatePlan() {
    // user explicitly wants plan creation now -> clear "skipped" flag
    try {
      if (user) localStorage.removeItem(skipKeyFor(user));
    } catch {}
    nav("/onboarding");
  }

  async function handleDeletePlan() {
    const ok = window.confirm(t("plan.deleteConfirm"));
    if (!ok) return;

    try {
      setDeleting(true);

      await api(`/me/plans/${encodeURIComponent(planId)}`, {
        method: "DELETE",
        invalidatePrefixes: [
          "/me/plans",
          "/me/plan/schedule",
          "/me/plan/",
          "/me/plan-sets",
          "/me/preferences",
        ],
      });

      nav("/plan");
    } catch (err) {
      console.error("Failed to delete plan", err);
      alert(err?.message || t("plan.deleteError"));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <Navbar user={user} onLogout={onLogout} tab="plan" />

      <div className="page-wrapper">
        <div className="page-container plan-container">
          <main className="plan-stack">
            {loading ? (
              <div className="glass plan-glass">
                <p>{t("common.loading")}</p>
              </div>
            ) : !plan ? (
              <div className="glass plan-glass plan-empty">
                <h2>
                  {t("plan.noPlanDetailTitle", {
                    defaultValue: "No plan to show",
                  })}
                </h2>

                <p className="plan-empty-text">
                  {t("plan.noPlanDetailText", {
                    defaultValue:
                      "This plan doesn’t exist anymore (or you don’t have any plans yet).\nCreate your first plan to start training.",
                  })}
                </p>

                <div className="plan-empty-actions" style={{ gap: 10 }}>
                  <button className="btn primary" type="button" onClick={goCreatePlan}>
                    {t("plan.createButton")}
                  </button>

                  <button className="btn" type="button" onClick={() => nav("/plan")}>
                    {t("common.back", { defaultValue: "Back" })}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="glass plan-glass">
                  <div className="plan-detail-header">
                    <h2 style={{ margin: 0 }}>{t("plan.currentPlanTitle")}</h2>
                    <button className="btn" type="button" onClick={() => nav("/plan")}>
                      {t("common.back", { defaultValue: "Back" })}
                    </button>
                  </div>

                  <div className="prefs-bubbles">
                    <div className="bubble">
                      <Target size={18} /> {plan.goal}
                    </div>
                    <div className="bubble">
                      <MapPin size={18} /> {plan.trainingPlace}
                    </div>
                    <div className="bubble">
                      <CalendarDays size={18} /> {plan.trainingDays?.join(", ")}
                    </div>
                    <div className="bubble">
                      <Clock size={18} /> {plan.planStart}
                    </div>
                    <div className="bubble">
                      <Timer size={18} /> {plan.planDuration} {monthsLabel}
                    </div>
                    {endDate ? (
                      <div className="bubble">
                        <CalendarCheck2 size={18} /> {endDate}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="glass plan-glass">
                  <h2>{t("plan.previewTitle")}</h2>

                  {preview ? (
                    <div className="preview">
                      <Calendar
                        marked={marked}
                        onDaySelect={(ymd) => nav(`/plan/day/${ymd}`, { state: { fromPlanId: planId } })}
                        />
                    </div>
                  ) : (
                    <p style={{ opacity: 0.85 }}>
                      {t("plan.noPreview", {
                        defaultValue: "No schedule preview available for this plan.",
                      })}
                    </p>
                  )}

                  <div className="plan-actions">
                    <button
                      className="btn danger"
                      type="button"
                      onClick={handleDeletePlan}
                      disabled={deleting}
                      title={t("plan.deleteTitle")}
                    >
                      {deleting ? t("plan.deleting") : t("plan.deleteButton")}
                    </button>
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </>
  );
}